import { useCallback, useEffect, useState } from 'react';
import { SystemFeedback } from 'cat-ecommerce-alloy';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
  FITMENT_VARIANTS,
  PRODUCT_VARIANTS,
  REPLACEMENT_LIST,
  STATUS,
  BUNDLE_BEAN,
  ESPOT_VARIANTS,
  PAGE_BREAKPOINTS,
  BEST_SELLER_GA_LIST_NAME,
  PRODUCT_STATUS_DISCONTINUED
} from '@app/constants/commonConstants';
import { ERROR_DOMAIN, ERROR_PATH } from '@app/constants/errorConstants';
import { isEmpty } from '@app/utils';
import Espot from '@app/components/common/Espot/Espot';
import WarrantyReturnPolicy from '@app/components/common/WarrantyReturnPolicy';
import useSystemFeedback from '@app/hooks/useSystemFeedback';
import { useBreakpoint } from '@app/hooks';
import ExceptionFeedbackBySelector from '../../../common/Exception/ExceptionFeedbackBySelector';
import ProductPrimaryActions from '../../../common/product/ProductPrimaryActions';
import ProductFitment from '../../../common/ProductFitment';
import FrequentlyBoughtTogether from '../FrequentlyBoughtTogether';
import ProductAvailability from './ProductAvailability';
import ProductGallery from './ProductGallery';
import ProductPriceDetails from './ProductPriceDetails';
import ProductPrimaryAttributes from './ProductPrimaryAttributes';
import ProductSpecials from './ProductSpecials';
import ProductSystemFeedback from './ProductSystemFeedback';
import ProductTitle from './ProductTitle';
import ProductRemanParts from './ProductRemanParts';
import { CSR_TEXTS } from '../../csr-landing/texts';
import { getFbtItem, getJobRecommendations } from '@app/store/products/actions';
import JobRecommendations from '../JobRecommendations';
import styles from '../styles.module.scss';
import { useLocation } from 'react-router';
import queryString from 'query-string';
import ProductSystemFeedbackBlocks from './ProductSystemFeedbackBlocks';
import { CatButton } from '@blocks-react/components/Button';

const UNKNOWN_ERROR_TITLE = 'UNKNOWN_ERROR_TITLE';
const FEEDBACK_TYPE_ERROR = 'error';

const ProductOverview = ({
  espotHtml = '',
  fitmentEnabled,
  initialPriceCallMade,
  priceCallStatus,
  quantity,
  setQuantity,
  didFitmentCallResolve,
  getFitmentResult,
  getFitmentCheck,
  isPackage,
  isPackageWithItemAssociated,
  selectedQuantity
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [quantityError, setQuantityError] = useState();
  const { clearError, setError } = useSystemFeedback();
  const searchParams = useLocation().search;
  const { from } = queryString.parse(searchParams);
  const isLG = useBreakpoint(PAGE_BREAKPOINTS.LG);
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const isCatCorp = useSelector(s => s.common.isCatCorp);
  const isPriceCallComplete =
    priceCallStatus === STATUS.RESOLVED || priceCallStatus === STATUS.REJECTED;
  const isLoading = priceCallStatus === STATUS.PENDING;
  const current = useSelector(s => s.products?.current);
  const partNumber = useSelector(s => s.products?.byId?.[current]?.partNumber);

  const indirectReplacement = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.indirectReplacement
  );
  const replacementParts = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.replacementParts
  );
  const isReplacedPart = !isEmpty(replacementParts) || indirectReplacement;
  const [specials] =
    useSelector(s => s.products.byId?.[current]?.specials) || [];

  const unitPrice = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.discountedPrice
  );
  const unformattedUnitPrice = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.unformattedUnitPrice
  );
  const formattedZeroPrice = useSelector(
    s => s.dealerCustomer?.formattedZeroPrice
  );
  const enableAddToCart = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.enableAddToCart
  );
  const isZeroPrice = unitPrice === formattedZeroPrice;
  const showPriceAndAvailability =
    (enableAddToCart && unitPrice && !isZeroPrice && !isReplacedPart) ||
    isLoading;

  const name = useSelector(s => s.products.byId?.[current]?.name);
  const categoryPath = useSelector(
    s => s.products.byId?.[current]?.categoryPath
  );
  const manufacturer = useSelector(
    s => s.products.byId?.[current]?.manufacturer
  );
  const availability = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.availability
  );
  const sourcingDetails = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.sourcingDetails
  );

  const weight = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.weight
  );

  const model = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.model
  );

  const isCustomizable =
    useSelector(s => s.products.byId?.[current]?.isCustomizable) ?? false;
  const beanType = useSelector(s => s.products.byId?.[current]?.type);
  const isBundle = beanType === BUNDLE_BEAN;

  const currencyCode = useSelector(s => s.dealerCustomer?.currency) ?? '';
  const [productClickDetails, setproductClickDetails] = useState([]);

  const replacementList = REPLACEMENT_LIST;
  const fbtItems = useSelector(s => s.products?.fbtItems?.products) || [];
  const isBuyOnBehalf = useSelector(s => s.common?.isBuyOnBehalf) || false;
  const errorAdding = useSelector(
    s => s.errors?.[ERROR_DOMAIN.PDP]?.[ERROR_PATH.MAIN]
  );
  const serialNumber = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.serialNumber
  );

  const showJobsRecommendations = serialNumber;

  const productStatus = useSelector(s => s.products.byId?.[current]?.status);
  const PCC_DiscontinuedParts = useSelector(
    state => state.featureFlag.PCC_DiscontinuedPartsInformationOnPDP
  );
  const testFeature = useSelector(state => state.featureFlag);
  console.log('testFeature', testFeature);
  const isDiscontinued =
    PCC_DiscontinuedParts && productStatus === PRODUCT_STATUS_DISCONTINUED;

  const catEntryId = useSelector(
    s => s.products?.byId?.[current]?.itemCatEntryId
  );

  const quantityAvailable = Number(
    useSelector(
      s =>
        s.products.priceAndAvailability?.[partNumber]?.availability[0]
          ?.quantityAvailable
    )
  );

  const getExceptionBlock = () => {
    if (errorAdding?.title === UNKNOWN_ERROR_TITLE && isBuyOnBehalf) {
      return (
        <SystemFeedback
          message={CSR_TEXTS.CSR_REFRESH_ERROR_MESSAGE_IN_PDP}
          title={t(UNKNOWN_ERROR_TITLE)}
          type={FEEDBACK_TYPE_ERROR}
        />
      );
    }

    return (
      <ExceptionFeedbackBySelector
        selector={s => s.errors?.[ERROR_DOMAIN.PDP]?.[ERROR_PATH.MAIN]}
        showReferenceId
      />
    );
  };

  useEffect(() => {
    if (replacementParts?.length && isPriceCallComplete) {
      setproductClickDetails(
        replacementParts.map((part, index) => ({
          availability: part.availability,
          sourcingDetails: part.sourcingDetails,
          name: part.name,
          partNumber: part.partNumber,
          brand: 'CAT',
          quantity: part.quantity,
          category: part.categoryPath,
          price: part.unformattedUnitPrice,
          currencyCode,
          businessUnit: 'PCC',
          productWeight: part.weight,
          list: replacementList,
          position: index + 1,
          checkFitment: getFitmentCheck(part.partNumber),
          relatedModel: model
        }))
      );
    } else if (isPriceCallComplete) {
      setproductClickDetails([
        {
          availability: availability,
          sourcingDetails: sourcingDetails,
          name,
          partNumber,
          brand: manufacturer,
          quantity,
          category: categoryPath,
          price: unformattedUnitPrice,
          currencyCode,
          businessUnit: 'PCC',
          productWeight: weight,
          checkFitment: getFitmentCheck(partNumber),
          relatedModel: model,
          list: from === 'bestSeller' ? BEST_SELLER_GA_LIST_NAME : ''
        }
      ]);
    }
  }, [
    availability,
    sourcingDetails,
    categoryPath,
    currencyCode,
    getFitmentCheck,
    isPriceCallComplete,
    manufacturer,
    model,
    name,
    partNumber,
    quantity,
    replacementList,
    replacementParts,
    unformattedUnitPrice,
    weight,
    from
  ]);

  // Fetches FBT Items
  useEffect(() => {
    if (isCatCorp) {
      partNumber && dispatch(getFbtItem(partNumber, isLG));
    } else {
      partNumber &&
        isPriceCallComplete &&
        dispatch(getFbtItem(partNumber, isLG));
    }
  }, [partNumber, model, dispatch, isLG, isCatCorp, isPriceCallComplete]);

  //Fetches Job Recomm Data
  useEffect(() => {
    if (serialNumber) {
      dispatch(getJobRecommendations(partNumber));
    }
  }, [dispatch, partNumber, serialNumber]);

  const handleAddToCartError = useCallback(
    error => {
      if (error) {
        setError(ERROR_DOMAIN.PDP, ERROR_PATH.MAIN, error);
      } else {
        clearError(ERROR_DOMAIN.PDP, ERROR_PATH.MAIN);
      }
    },
    [clearError, setError]
  );

  const getFbtSection = value => {
    return (
      fbtItems?.length > 0 && (
        <FrequentlyBoughtTogether
          getFitmentResult={getFitmentResult}
          model={model}
        />
      )
    );
  };
  const getReplacedPartsSection = value => {
    return (
      value && (
        <ProductPrimaryActions
          handleAddToCartError={handleAddToCartError}
          priceCallStatus={priceCallStatus}
          productClickDetails={productClickDetails}
          productId={partNumber}
          quantity={quantity}
          quantityError={quantityError}
          setQuantity={setQuantity}
          setQuantityError={setQuantityError}
          variant={PRODUCT_VARIANTS.pdp_overview}
          suppressLoader={initialPriceCallMade} // Don't show loader after first load
          isPackage={isPackage}
          isPackageWithItemAssociated={isPackageWithItemAssociated}
          selectedQuantity={selectedQuantity}
          partNumber={partNumber}
          isDiscontinued={isDiscontinued}
          quantityAvailable={quantityAvailable}
        />
      )
    );
  };
  return (
    <section className="row">
      <div
        className={cx('col-12 col-md-6', {
          [`${styles['col-custom-40']}`]: isLG
        })}
      >
        <ProductGallery isDiscontinued={isDiscontinued} />
        {!isPackage && !isBundle && (
          <Espot
            className="d-none d-lg-block"
            html={espotHtml}
            variant={ESPOT_VARIANTS.LARGE}
          />
        )}
      </div>
      <div
        className={cx('col-12 col-md-6 mt-3 mt-lg-0', {
          [`${styles['col-custom-60']}`]: isLG
        })}
      >
        <ProductTitle productId={current} />
        {isDiscontinued && (
          <ProductSystemFeedbackBlocks
            quantityAvailable={quantityAvailable}
            replacementParts={replacementParts}
            partNumber={partNumber}
            catEntryId={catEntryId}
          />
        )}
        <ProductPrimaryAttributes productId={current} />
        {getExceptionBlock()}
        {!isDiscontinued && (
          <ProductSystemFeedback
            indirectReplacement={indirectReplacement}
            replacementParts={replacementParts}
            isPackage={isPackage}
            isCustomizable={isCustomizable && isPackageWithItemAssociated}
          />
        )}
        {!isReplacedPart && (
          <ProductPriceDetails
            productId={partNumber}
            uniqueId={current}
            showPrice={showPriceAndAvailability}
            isPriceCallComplete={isPriceCallComplete}
            isLoading={isLoading && !initialPriceCallMade} // Don't show loader after first load
          />
        )}
        {specials && <ProductSpecials specials={specials} />}
        {fitmentEnabled && !isBundle && (
          <ProductFitment
            className="py-3"
            partNumber={partNumber}
            variant={FITMENT_VARIANTS.large}
            didFitmentCallResolve={didFitmentCallResolve}
            getFitmentResult={getFitmentResult}
          />
        )}
        {replacementParts?.length >= 1 &&
          isDiscontinued &&
          quantityAvailable > 0 && (
            <CatButton disabled>{t('DISCONTINUED_CTA')}</CatButton>
          )}
        <div
          className={isReplacedPart ? 'd-none d-lg-block' : 'd-none d-md-block'}
        >
          {getReplacedPartsSection(isLG)}
        </div>

        {showPriceAndAvailability && (
          <ProductAvailability
            productId={partNumber}
            isLoading={isLoading && initialPriceCallMade} // Don't show loader on first load
            quantity={quantity}
            showStoreAvailability
          />
        )}

        <div
          className={
            isReplacedPart ? 'mt-3 d-none d-lg-block' : 'mt-3 d-none d-md-block'
          }
        >
          <WarrantyReturnPolicy
            className={`flex-lg-row align-items-lg-center ${styles['product-warranty-return--links']}`}
            separatorClassname="d-lg-inline"
          />
        </div>
        <div className="mt-5">
          <ProductRemanParts
            isPriceLoading={isLoading}
            isPriceCall={isPriceCallComplete}
          />
        </div>
        {showJobsRecommendations && isMD && <JobRecommendations />}
      </div>
      <div
        className={
          isReplacedPart ? 'col-12 d-block d-lg-none' : 'col-12 d-block'
        }
      >
        {getReplacedPartsSection(!isLG)}
        {!isMD && (
          <div className="mt-3">
            <WarrantyReturnPolicy
              className={`flex-md-row align-items-md-center ${styles['product-warranty-return--links']}`}
              separatorClassname="d-md-inline"
            />
          </div>
        )}
        {showJobsRecommendations && !isMD && <JobRecommendations />}
      </div>
      <div className="col-12">{getFbtSection()}</div>
    </section>
  );
};

ProductOverview.propTypes = {
  espotHtml: PropTypes.string,
  fitmentEnabled: PropTypes.bool,
  initialPriceCallMade: PropTypes.bool,
  priceCallStatus: PropTypes.string,
  quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setQuantity: PropTypes.func,
  didFitmentCallResolve: PropTypes.bool,
  getFitmentResult: PropTypes.func,
  getFitmentCheck: PropTypes.func,
  isPackage: PropTypes.bool,
  isPackageWithItemAssociated: PropTypes.bool,
  selectedQuantity: PropTypes.number
};

export default ProductOverview;
